
/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import * as XLSX from "xlsx";

// 👉 Cambia aquí si alguna vez mueves la URL
const DEFAULT_LAMBDA_URL =
  "https://3erpc4vjx9.execute-api.us-east-1.amazonaws.com/prod/envio_outbound";

/**
 * ExportExcelWidget
 * Props:
 * - records: array con los registros a exportar (obligatorio)
 * - lambdaUrl: string con la URL del Lambda (opcional, por defecto DEFAULT_LAMBDA_URL)
 * - filename: nombre de archivo a generar (opcional, por defecto "llamadas.xlsx")
 * - mapRecord: función opcional para mapear cada item (si la pasas, reemplaza el default)
 */
export default function ExportExcelWidget({
  records = [],
  lambdaUrl = DEFAULT_LAMBDA_URL,
  filename = "llamadas.xlsx",
  mapRecord,
}) {
  const [email, setEmail] = useState("");
  const [customFilename, setCustomFilename] = useState(filename);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  // ======== 👇 MAPEADOR POR DEFECTO: ESQUEMA "TABLA" =========
  // Envía exactamente los campos que tu Lambda de "tabla" espera.
  // Si algún campo no existe, se envía vacío para no romper.
  const defaultMapperTabla = (i) => ({
    nombres: i?.nombres || "",
    phoneNumber: i?.phoneNumber || "",
    cedula: String(i?.cedula ?? ""),
    estado: i?.estado || "",
    nombre_campania: i?.nombre_campania || "",
    flujo_campania: i?.flujo_campania || "",
    tipoflujo: i?.tipoflujo || "",
    intentos: i?.intentos ?? "",
    intentos_actuales: i?.intentos_actuales ?? "",
    tiempo_entre_llamadas: i?.tiempo_entre_llamadas
      ? `${parseInt(i.tiempo_entre_llamadas)} min`
      : "",
    // opcional
    ultimo_intento: i?.ultimo_intento || "",
  });

  // Si el caller provee su propio mapRecord, lo usamos; si no, usamos el de tabla
  const mapper = typeof mapRecord === "function" ? mapRecord : defaultMapperTabla;

  const canSend = email.trim().length > 3 && Array.isArray(records) && records.length > 0;

  const getFinalFilename = () => {
    let name = (customFilename || "llamadas.xlsx").trim();
    if (!name.toLowerCase().endsWith(".xlsx")) {
      name = name.replace(/\.(csv|xls|json)$/i, "") || "llamadas";
      name = `${name}.xlsx`;
    }
    return name;
  };

  async function handleExport() {
    setMsg("");
    if (!canSend) {
      setMsg("⚠️ Ingresa un correo y asegúrate de tener registros para exportar.");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        to_email: email.trim(),
        filename: getFinalFilename(),
        records: records.map(mapper),
      };

      const res = await fetch(lambdaUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);

      setMsg(`✅ ${data?.message || "Reporte enviado. Revisa tu correo."}`);
    } catch (e) {
      console.error(e);
      // Mantengo tu comportamiento original (mensaje de éxito aunque falle el fetch)
      setMsg(
        `✅ Reporte enviado. Revisa tu correo. ${
          e?.message ? `(${e.message})` : ""
        }`
      );
    } finally {
      setLoading(false);
    }
  }

  // 👉 NUEVO: descargar Excel localmente
  function handleDownload() {
    setMsg("");

    if (!Array.isArray(records) || records.length === 0) {
      setMsg("⚠️ No hay registros para descargar.");
      return;
    }

    try {
      const rows = records.map(mapper);
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);

      XLSX.utils.book_append_sheet(wb, ws, "Llamadas");
      XLSX.writeFile(wb, getFinalFilename());

      setMsg("✅ Archivo Excel descargado correctamente.");
    } catch (e) {
      console.error(e);
      setMsg(
        `❌ Error al generar el Excel para descargar. ${
          e?.message ? `(${e.message})` : ""
        }`
      );
    }
  }

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-header">
        <h3>Exportar Excel</h3>
        <i className="fas fa-file-excel"></i>
      </div>
      <div className="card-content">
        <div className="form-grid">
          <div className="form-group">
            <label>Correo (Gmail u otro)</label>
            <input
              type="email"
              placeholder="tu_correo@gmail.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{ minWidth: 260 }}
            />
          </div>
          <div className="form-group">
            <label>Nombre de archivo</label>
            <input
              type="text"
              placeholder="llamadas.xlsx"
              value={customFilename}
              onChange={(e) => setCustomFilename(e.target.value)}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            marginTop: 8,
            flexWrap: "wrap",
          }}
        >
          {/* Botón para ENVIAR por correo */}
          <button
            className="btn btn-primary"
            onClick={handleExport}
            disabled={!canSend || loading}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Enviando...
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane"></i> Enviar Excel
              </>
            )}
          </button>

          {/* Botón para DESCARGAR localmente */}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleDownload}
            disabled={!records || records.length === 0 || loading}
          >
            <i className="fas fa-download"></i> Descargar Excel
          </button>

          <span style={{ fontSize: 13, opacity: 0.8 }}>
            {Array.isArray(records)
              ? `${records.length} registros listos`
              : "0 registros"}
          </span>
        </div>

        {msg && (
          <div
            className={`alert ${
              msg.startsWith("✅")
                ? "alert-success"
                : msg.startsWith("❌")
                ? "alert-error"
                : "alert-info"
            }`}
            style={{ marginTop: 12 }}
          >
            {msg}
          </div>
        )}
      </div>
    </div>
  );
}
